import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Alert, Button } from "@medusajs/ui";
import { AlertTriangle } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useDeleteAddress } from "@/queries/customer/addresses";
import { handleActionError } from "@/lib/errors";

interface DeleteAddressDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** ID of the address to delete */
  addressId: string | null;
  /** Name of the address for display */
  addressName?: string;
  /** Callback when address is deleted successfully */
  onSuccess?: () => void;
}

/**
 * Confirmation dialog for deleting an address
 *
 * Features:
 * - Clear warning message
 * - Confirms destructive action
 * - Shows loading state during deletion
 * - Handles errors gracefully
 * - Accessible dialog structure
 */
export const DeleteAddressDialog = memo(function DeleteAddressDialog({
  open,
  onOpenChange,
  addressId,
  addressName,
  onSuccess
}: DeleteAddressDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const deleteMutation = useDeleteAddress();

  const handleClose = useCallback(() => {
    setServerError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDelete = useCallback(async () => {
    if (!addressId) return;

    setServerError(null);

    try {
      await deleteMutation.mutateAsync(addressId);
      handleClose();
      onSuccess?.();
    } catch (error) {
      setServerError(
        handleActionError(error, {
          boundary: "delete address dialog",
          fallback: "We couldn't delete this address. Please try again."
        })
      );
    }
  }, [addressId, deleteMutation, handleClose, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        aria-describedby="delete-dialog-description"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle
                className="h-5 w-5 text-red-600 dark:text-red-400"
                aria-hidden="true"
              />
            </div>
            <DialogTitle>Delete address</DialogTitle>
          </div>
          <DialogDescription
            id="delete-dialog-description"
            className="pt-2 text-left"
          >
            Are you sure you want to delete{" "}
            <span className="font-medium">
              {addressName ? `"${addressName}"` : "this address"}
            </span>
            ? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {serverError && (
          <Alert variant="error" dismissible>
            {serverError}
          </Alert>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            isLoading={deleteMutation.isPending}
            disabled={deleteMutation.isPending}
          >
            Delete address
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
